import { useCallback, useEffect, useRef, useState } from 'react';
import { type CorpusResponse, type KnowledgeCorpusEntityState } from 'shared/api/copilot';
import { type PersistedKnowledgeSource, toKnowledgeCorpusStub } from 'shared/copilot/utils/copilotSourceSelectionStorage';
interface AgentStateWriter {
  state: unknown;
  setState: (state: Record<string, unknown>) => void;
}
interface UseCopilotKnowledgeSourcesSessionArgs {
  agent: AgentStateWriter;
  /** Picker clicks only — not persisted hydrates. */
  onUserSelectionChange?: (sources: KnowledgeCorpusEntityState[]) => void;
}
interface UseCopilotKnowledgeSourcesSessionResult {
  selectedKnowledgeSourceIds: string[];
  selectedKnowledgeSources: KnowledgeCorpusEntityState[];
  hasSelectedKnowledgeSource: boolean;
  setSelectedKnowledgeSources: (corpusIds: string[], corpora: CorpusResponse[]) => void;
  clearSelectedKnowledgeSource: (corpusId?: string) => void;
  /** Replace picker + agent state from a persisted snapshot (chat hydrate / new-chat seed). */
  applyPersistedKnowledgeSources: (sources: PersistedKnowledgeSource[]) => void;
  /**
   * Drop ids missing from live corpora and rebuild full entities. Does not
   * treat this as a user click (no last-used write).
   */
  reconcileWithAvailableCorpora: (corpora: CorpusResponse[]) => void;
  /** Merge selected knowledge corpora into agent.state (does not touch `entities`). */
  syncAgentKnowledgeSourcesState: (sources: KnowledgeCorpusEntityState[]) => void;
}
const toEntityState = (corpus: CorpusResponse): KnowledgeCorpusEntityState => ({
  id: corpus.id,
  type: 'knowledge_corpus',
  name: corpus.name,
  data: corpus
});
const readAgentKnowledgeSources = (agentState: unknown): KnowledgeCorpusEntityState[] => {
  if (agentState == null || typeof agentState !== 'object') {
    return [];
  }
  const raw = (agentState as Record<string, unknown>).selected_knowledge_sources;
  return Array.isArray(raw) ? raw as KnowledgeCorpusEntityState[] : [];
};
const areKnowledgeSourcesEqual = (left: readonly KnowledgeCorpusEntityState[], right: readonly KnowledgeCorpusEntityState[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((entry, index) => {
    const other = right[index];
    return other?.id === entry.id && other?.type === entry.type;
  });
};

/** Persist stubs use `createdAt: ''`; live corpora always carry a real timestamp. */
const isResolvedKnowledgeEntity = (source: KnowledgeCorpusEntityState): boolean => Boolean(source.data.createdAt);

/**
 * Owns Copilot knowledge-source picker state and syncs it to
 * `agent.state.selected_knowledge_sources` (not `entities`).
 */
export const useCopilotKnowledgeSourcesSession = ({
  agent,
  onUserSelectionChange
}: UseCopilotKnowledgeSourcesSessionArgs): UseCopilotKnowledgeSourcesSessionResult => {
  const onUserSelectionChangeRef = useRef(onUserSelectionChange);
  onUserSelectionChangeRef.current = onUserSelectionChange;
  const [selectedKnowledgeSourceIds, setSelectedKnowledgeSourceIds] = useState<string[]>([]);
  const [selectedKnowledgeSources, setSelectedKnowledgeSourcesState] = useState<KnowledgeCorpusEntityState[]>([]);
  const syncAgentKnowledgeSourcesState = useCallback((sources: KnowledgeCorpusEntityState[]) => {
    const currentState = agent.state != null && typeof agent.state === 'object' ? {
      ...(agent.state as Record<string, unknown>)
    } : {};
    agent.setState({
      ...currentState,
      selected_knowledge_sources: sources
    });
  }, [agent]);
  useEffect(() => {
    if (areKnowledgeSourcesEqual(readAgentKnowledgeSources(agent.state), selectedKnowledgeSources)) {
      return;
    }
    syncAgentKnowledgeSourcesState(selectedKnowledgeSources);
  }, [agent.state, selectedKnowledgeSources, syncAgentKnowledgeSourcesState]);
  const setSelectedKnowledgeSources = useCallback((corpusIds: string[], corpora: CorpusResponse[]) => {
    const uniqueIds = [...new Set(corpusIds)];
    const byId = new Map(corpora.map(corpus => [corpus.id, corpus]));
    const entities = uniqueIds.map(id => byId.get(id)).filter((corpus_0): corpus_0 is CorpusResponse => corpus_0 != null).map(toEntityState);
    setSelectedKnowledgeSourceIds(entities.map(entity => entity.id));
    setSelectedKnowledgeSourcesState(entities);
    syncAgentKnowledgeSourcesState(entities);
    onUserSelectionChangeRef.current?.(entities);
  }, [syncAgentKnowledgeSourcesState]);
  const clearSelectedKnowledgeSource = useCallback((corpusId?: string) => {
    if (corpusId == null) {
      setSelectedKnowledgeSourceIds([]);
      setSelectedKnowledgeSourcesState([]);
      syncAgentKnowledgeSourcesState([]);
      onUserSelectionChangeRef.current?.([]);
      return;
    }
    const next = selectedKnowledgeSources.filter(source => source.id !== corpusId);
    setSelectedKnowledgeSourceIds(next.map(source_0 => source_0.id));
    setSelectedKnowledgeSourcesState(next);
    syncAgentKnowledgeSourcesState(next);
    onUserSelectionChangeRef.current?.(next);
  }, [selectedKnowledgeSources, syncAgentKnowledgeSourcesState]);
  const applyPersistedKnowledgeSources = useCallback((sources_0: PersistedKnowledgeSource[]) => {
    const stubs = sources_0.map(toKnowledgeCorpusStub);
    setSelectedKnowledgeSourceIds(stubs.map(source_1 => source_1.id));
    setSelectedKnowledgeSourcesState(stubs);
    syncAgentKnowledgeSourcesState(stubs);
  }, [syncAgentKnowledgeSourcesState]);
  const reconcileWithAvailableCorpora = useCallback((corpora_0: CorpusResponse[]) => {
    if (selectedKnowledgeSourceIds.length === 0) {
      return;
    }
    const byId_0 = new Map(corpora_0.map(corpus_1 => [corpus_1.id, corpus_1]));
    const nextIds = selectedKnowledgeSourceIds.filter(id_0 => byId_0.has(id_0));
    const entities_0 = nextIds.map(id_1 => toEntityState(byId_0.get(id_1) as CorpusResponse));
    const idsUnchanged = nextIds.length === selectedKnowledgeSourceIds.length && nextIds.every((id_2, index) => id_2 === selectedKnowledgeSourceIds[index]);
    const alreadyResolved = idsUnchanged && selectedKnowledgeSources.every(isResolvedKnowledgeEntity);
    if (alreadyResolved) {
      return;
    }
    setSelectedKnowledgeSourceIds(nextIds);
    setSelectedKnowledgeSourcesState(entities_0);
    syncAgentKnowledgeSourcesState(entities_0);
  }, [selectedKnowledgeSourceIds, selectedKnowledgeSources, syncAgentKnowledgeSourcesState]);
  return {
    selectedKnowledgeSourceIds,
    selectedKnowledgeSources,
    hasSelectedKnowledgeSource: selectedKnowledgeSources.length > 0,
    setSelectedKnowledgeSources,
    clearSelectedKnowledgeSource,
    applyPersistedKnowledgeSources,
    reconcileWithAvailableCorpora,
    syncAgentKnowledgeSourcesState
  };
};